import { useEffect, useId, useReducer, useRef, useState, type CSSProperties, type FormEvent, type MouseEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { ChevronRight } from 'lucide-react'
import { keys } from '../../api/keys'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { useToast } from '../../platform/feedback/ToastProvider'
import { CatalogueIconImg } from '../../platform/icons/CatalogueIconImg'
import { Icon } from '../../platform/icons/Icon'
import { IconCataloguePicker } from '../../platform/icons/IconCataloguePicker'
import type { CatalogueIcon } from '../../platform/icons/useIconCatalogue'
import { Overlay } from '../../platform/overlay/Overlay'
import { Button } from '../../platform/primitives/Button'
import { Checkbox } from '../../platform/primitives/Checkbox'
import { AlertDialog, ConfirmDialog } from '../../platform/primitives/Dialog'
import { Field } from '../../platform/primitives/Field'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Money } from '../../platform/primitives/Money'
import { AlertPanel, Panel } from '../../platform/primitives/Panel'
import { Select } from '../../platform/primitives/Select'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { accountLabel } from '../accounts/accountLabel'
import { useAccounts } from '../accounts/useAccounts'
import { useCategories } from '../categories/useCategories'
import { getTransaction, useTransaction } from '../transactions/useTransaction'
import { RecurringBackLink, RemovedBill } from './BillChrome'
import { ERROR_ORDER, draftOf, draftReducer, sameDraft, validateDraft, type Draft, type DraftErrors } from './recurringFormDraft'
import { RECURRING_PATH, recurringDetailPath } from './recurringPaths'
import { formatDue, type LocalDate } from './recurringRules'
import { PICKABLE_FREQUENCIES, frequencyLabel, type Bill, type Frequency } from './recurringWire'
import { useBill } from './useRecurring'
import { FIND_FAILED, PeriodLinkError, findPastCopy, usePeriodLinks } from './usePeriodLinks'
import {
  FALLBACK_CURRENCY,
  SAVE_COPY,
  SaveError,
  useSaveRecurring,
  type FormMode,
  type Saved,
  type SourceTxn,
} from './useSaveRecurring'
import './recurring.css'

/** iOS `applyToPastNote`, verbatim. */
export const APPLY_NOTES = {
  noAccount: "Pick the account it's paid from to match past payments",
  off: 'Only transactions that arrive from now on',
  on: 'Also links matching transactions you already have, back to that date',
} as const

const DISCARD = {
  title: 'Discard changes?',
  /** NEW COPY: `ConfirmDialog.body` is required and iOS has no message. */
  body: "Your changes to this bill won't be saved.",
  confirm: 'Discard changes',
  keep: 'Keep editing',
} as const

const STILL_SAVING = {
  title: 'Still saving',
  body: "This bill is already on its way to the server, so it can't be discarded. Wait for the save to finish.",
} as const

const FIND_PAST_ASK = {
  title: 'Look for past payments now?',
  body: 'Something that affects matching changed, so past transactions may now match.',
  confirm: 'Look now',
  cancel: 'Not now',
} as const

/** The add form's way back: the list, as the list's own header names it. */
const NEW_BACK_LABEL = 'Recurring payments'

const transactionPath = (id: string) => `/transactions/${encodeURIComponent(id)}`

/** The 409's `detail.recurring_id`, or null when the body does not carry one. */
function recurringIdOf(cause: unknown): string | null {
  if (typeof cause !== 'object' || cause === null || !('detail' in cause)) return null
  const { detail } = cause
  if (typeof detail !== 'object' || detail === null || !('recurring_id' in detail)) return null
  return typeof detail.recurring_id === 'string' && detail.recurring_id !== '' ? detail.recurring_id : null
}

// ------------------------------------------------------------------ screens

/** `/recurring/new`, or `/recurring/new?from=<transaction id>` (M7-R17). */
export function RecurringNew() {
  const from = useSearchParams()[0].get('from')
  return from === null || from === '' ? <RecurringForm mode={{ kind: 'create' }} /> : <RecurringFromTransaction id={from} />
}

/**
 * The form seeded from one transaction. It opens only once the transaction has
 * loaded: the prefill is the point, and a form that filled itself in under the
 * user's cursor would lose what they had typed. A transaction that is gone
 * says so, with the way out `useTransaction` provides.
 */
function RecurringFromTransaction({ id }: { id: string }) {
  const txn = useTransaction(id)
  return (
    <Async
      surface="transaction-detail"
      state={txn}
      skeleton={<FormSkeleton />}
      // Unreachable: a transaction query never yields empty. Required by Async.
      empty={{ title: 'That transaction is no longer available.' }}
    >
      {/* Keyed by id: a different transaction is a different form. */}
      {(t) => <RecurringForm key={t.id} mode={{ kind: 'fromTransaction', txn: t }} />}
    </Async>
  )
}

/**
 * `/recurring/:id/edit`. The form never opens on a removed bill, and opens
 * only once the bill has loaded — it starts from the bill's values. A bill
 * removed while the form is open replaces it with the detail's own removed
 * state, focus on its heading.
 */
export function RecurringEdit() {
  const { id = '' } = useParams()
  const bill = useBill(id)
  // Set once the bill has been on screen (derived-state pattern), so a removal
  // mid-session moves focus, as the detail does.
  const [seen, setSeen] = useState(false)
  if (bill.status === 'success' && !bill.removed && !seen) setSeen(true)
  if (bill.removed) {
    return (
      <div className="rec-page rec-form">
        <RecurringBackLink />
        <RemovedBill focus={seen} />
      </div>
    )
  }
  return (
    <Async
      surface="recurring-detail"
      state={bill}
      skeleton={<FormSkeleton />}
      // Unreachable: a bill query never yields empty. Required by Async.
      empty={{ title: 'This bill was removed' }}
    >
      {/* Keyed by id: a different bill is a different form, never a re-seeded one. */}
      {(b) => <RecurringForm key={b.id} mode={{ kind: 'edit', bill: b }} />}
    </Async>
  )
}

function FormSkeleton() {
  return (
    <div className="rec-page rec-form">
      <Skeleton height={14} width={160} />
      <Skeleton height={26} width={200} />
      <Panel className="rec-form__panel">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} height={44} />
        ))}
      </Panel>
    </div>
  )
}

// --------------------------------------------------------------------- form

/**
 * Add and Edit a bill (D5, S4, S13, S15, iOS Q28) — a page, not a modal, so
 * the URL is real, Back works, and the icon picker is the one overlay.
 *
 * **The mode is frozen at mount.** The edit screen re-renders this with the
 * refetched bill after a 409; adopting it would hand the next Save the other
 * device's `version` and overwrite what it changed without the user ever
 * seeing it (Review Focus 4). The form says "Close and reopen it" instead.
 *
 * **Validation runs on submit** and focuses the first invalid field; a field's
 * error clears when it changes. **Save is never disabled**: while a save is
 * out it reads "Saving…" and is `aria-disabled`, and `useSaveRecurring` folds
 * any further submit into the one in flight.
 *
 * **Leaving with changes asks first** (iOS V136) — Cancel, the back link and a
 * tab close or reload (`beforeunload`). The sidebar cannot be intercepted
 * under `BrowserRouter` (Ruling 21).
 */
function RecurringForm(props: { mode: FormMode }) {
  const [mode] = useState(props.mode)
  const isEdit = mode.kind === 'edit'
  const fromTxn = mode.kind === 'fromTransaction' ? mode.txn : null
  const bill = mode.kind === 'edit' ? mode.bill : null
  const navigate = useNavigate()
  const toast = useToast()
  const bp = useBreakpoint()
  const pairs = bp === 'desktop' || bp === 'wide'
  const accounts = useAccounts()
  const accountList = accounts.status === 'success' || accounts.status === 'stale' ? accounts.data : []

  const [initial] = useState(() => draftOf(mode))
  const [draft, dispatch] = useReducer(draftReducer, initial)
  const [errors, setErrors] = useState<DraftErrors>({})
  const [saveError, setSaveError] = useState<string | null>(null)
  /** A 409 from a transaction: the bill it already belongs to, once known. */
  const [takenBy, setTakenBy] = useState<string | null>(null)
  const client = useQueryClient()
  // The 409 lookup may outlive the form; it must not set state on an unmounted one.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const [pickedIcon, setPickedIcon] = useState<CatalogueIcon | null>(null)
  const [iconOpen, setIconOpen] = useState(false)
  const [leaving, setLeaving] = useState<string | null>(null)
  const [stillSaving, setStillSaving] = useState(false)
  const [askFindPast, setAskFindPast] = useState<Bill | null>(null)
  // Set once a save lands, so the navigation it starts is never guarded.
  const done = useRef(false)
  // Set by the submit that sent the save. A second submit while it is out
  // returns at once: only the first caller toasts, navigates or asks.
  const submitting = useRef(false)

  const account = accountList.find((a) => a.id === draft.accountId)
  // Display only: the label's currency. What is SENT is the account's (the
  // server derives it) or, with none, the one fallback (Ruling 44).
  const currency = account?.currency ?? bill?.currency ?? fromTxn?.currency ?? FALLBACK_CURRENCY
  const { save, saving } = useSaveRecurring(mode, FALLBACK_CURRENCY)
  const dirty = !sameDraft(draft, initial)

  const nameRef = useRef<HTMLInputElement>(null)
  const amountRef = useRef<HTMLInputElement>(null)
  const nextDueRef = useRef<HTMLInputElement>(null)
  const sinceRef = useRef<HTMLInputElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const iconRowRef = useRef<HTMLButtonElement>(null)
  const refs = { name: nameRef, amount: amountRef, nextDue: nextDueRef, payingSince: sinceRef }

  const applyNoteId = useId()
  const iconTarget = useHitTarget('form')
  const checkTarget = useHitTarget('page')

  // Create lands in Name, the first empty field; edit on the heading, so a
  // screen-reader user hears which form opened before landing in a field.
  useEffect(() => {
    if (isEdit) headingRef.current?.focus()
    else nameRef.current?.focus()
  }, [isEdit])

  useEffect(() => {
    if (!dirty) return
    const onUnload = (e: BeforeUnloadEvent) => {
      if (done.current) return
      e.preventDefault()
      // Chrome before 119 needs the legacy property set as well.
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onUnload)
    return () => window.removeEventListener('beforeunload', onUnload)
  }, [dirty])

  const backTo = bill ? recurringDetailPath(bill.id) : fromTxn ? transactionPath(fromTxn.id) : RECURRING_PATH
  const backLabel = bill ? bill.name : fromTxn ? fromTxn.merchant : NEW_BACK_LABEL

  function set<K extends keyof Draft>(key: K, value: Draft[K], clears?: keyof DraftErrors) {
    dispatch({ type: 'change', patch: { [key]: value } as Partial<Draft> })
    if (clears) setErrors((e) => ({ ...e, [clears]: undefined }))
  }

  function requestLeave(to: string) {
    if (saving || submitting.current) setStillSaving(true)
    else if (dirty && !done.current) setLeaving(to)
    else void navigate(to)
  }

  function onBack(e: MouseEvent) {
    if (saving || submitting.current || (dirty && !done.current)) {
      e.preventDefault()
      requestLeave(backTo)
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (submitting.current || done.current) return
    setSaveError(null)
    setTakenBy(null)
    const { values, errors: found } = validateDraft(draft, mode.kind)
    setErrors(found)
    const first = ERROR_ORDER.find((k) => found[k] !== undefined)
    if (first) {
      refs[first].current?.focus()
      return
    }
    submitting.current = true
    let saved: Saved | null
    try {
      saved = await save(values)
    } catch (err) {
      setSaveError(err instanceof SaveError ? err.message : SAVE_COPY.failed)
      if (fromTxn !== null && err instanceof SaveError && err.kind === 'conflict') void findOwner(fromTxn.id, err.cause)
      return
    } finally {
      submitting.current = false
    }
    done.current = true
    if (bill === null) {
      if (saved === null) return
      const due = saved.nextDue ? `Added · due ${formatDue(saved.nextDue, 'short')}` : 'Added'
      const linked = saved.linked === undefined ? '' : `. ${findPastCopy(saved.linked)}.`
      toast.show({ kind: 'success', message: `${due}${linked}` })
      void navigate(recurringDetailPath(saved.id), { replace: true })
      return
    }
    if (saved === null) {
      void navigate(backTo, { replace: true })
      return
    }
    toast.show({ kind: 'success', message: `${saved.bill.name} saved` })
    const matchingMoved = values.accountId !== initial.accountId || values.frequency !== initial.frequency
    if (matchingMoved && saved.bill.accountId !== null) setAskFindPast(saved.bill)
    else void navigate(recurringDetailPath(saved.id), { replace: true })
  }

  /**
   * Which bill already owns the transaction. The 409 body names it
   * (`detail.recurring_id`); only if it does not is the transaction asked, as a
   * query so the answer is cached and a stale detail screen learns it too.
   */
  async function findOwner(txnId: string, cause: unknown) {
    let owner = recurringIdOf(cause)
    if (owner === null) {
      try {
        const t = await client.fetchQuery({
          queryKey: keys.transactions.detail(txnId),
          queryFn: () => getTransaction(txnId),
          staleTime: 0,
        })
        owner = t.recurringStreamId
      } catch {
        // No link: the sentence above still says what happened.
      }
    }
    if (mounted.current) setTakenBy(owner)
  }

  const applyNote = draft.accountId === null ? APPLY_NOTES.noAccount : draft.applyToPast ? APPLY_NOTES.on : APPLY_NOTES.off
  const iconValue = pickedIcon?.label ?? (draft.icon !== null ? 'Current' : 'Default')
  const saveLabel = saving ? 'Saving…' : 'Save'

  const actions = (where: 'head' | 'foot') => (
    <div className={`rec-form__actions rec-form__actions--${where}`}>
      <Button type="button" variant="chip" size="page" onClick={() => requestLeave(backTo)}>
        Cancel
      </Button>
      <Button type="submit" variant="primary" size="page" aria-disabled={saving || undefined}>
        {saveLabel}
      </Button>
    </div>
  )

  const repeats = (
    <div className="rec-form__group">
      <span className="rec-form__group-label" aria-hidden="true">
        Repeats
      </span>
      <Select
        label="Repeats"
        size="form"
        value={draft.frequency}
        onChange={(e) => set('frequency', e.target.value as Frequency)}
      >
        {/* An unclassified bill shows what it is, and cannot be chosen back. */}
        {draft.frequency === 'unknown' && (
          <option value="unknown" disabled>
            {frequencyLabel('unknown')}
          </option>
        )}
        {PICKABLE_FREQUENCIES.map((f) => (
          <option key={f} value={f}>
            {frequencyLabel(f)}
          </option>
        ))}
      </Select>
    </div>
  )

  const paidFrom = (
    <div className="rec-form__group">
      <span className="rec-form__group-label" aria-hidden="true">
        Paid from (Optional)
      </span>
      <Select
        label="Paid from (Optional)"
        size="form"
        value={draft.accountId ?? ''}
        onChange={(e) => set('accountId', e.target.value === '' ? null : e.target.value)}
      >
        <option value="">No account</option>
        {/* While accounts load (or if they fail), the bill's own account stays
            the selected value rather than reading as "No account" — a Save
            then would not touch it, but the control would say otherwise. */}
        {draft.accountId !== null && account === undefined && (
          <option value={draft.accountId}>{accounts.status === 'loading' ? 'Loading accounts…' : 'Current account'}</option>
        )}
        {accountList.map((a) => (
          <option key={a.id} value={a.id}>
            {accountLabel(a)}
          </option>
        ))}
      </Select>
    </div>
  )

  return (
    <div className="rec-page rec-form">
      <RecurringBackLink to={backTo} label={backLabel} onClick={onBack} />
      <form className="rec-form__form" onSubmit={(e) => void submit(e)} noValidate>
        <div className="rec-form__head">
          <h2 ref={headingRef} tabIndex={-1} className="rec-form__title">
            {isEdit ? 'Edit recurring' : 'New recurring'}
          </h2>
          {actions('head')}
        </div>

        {saveError !== null && (
          <AlertPanel className="rec-form__error">
            <span role="alert">{saveError}</span>
            {takenBy !== null && (
              <InlineLink to={recurringDetailPath(takenBy)} className="rec-form__error-link">
                Open that recurring payment
              </InlineLink>
            )}
          </AlertPanel>
        )}

        <Panel className="rec-form__panel">
          <Field
            ref={nameRef}
            label="Name"
            size="form"
            placeholder="e.g. Netflix"
            autoComplete="off"
            value={draft.name}
            error={errors.name}
            onChange={(e) => set('name', e.target.value, 'name')}
          />

          {fromTxn !== null && <TransactionFacts txn={fromTxn} />}

          {fromTxn !== null ? (
            repeats
          ) : (
            <div className={pairs ? 'rec-form__pair' : 'rec-form__stack'}>
              <Field
                ref={amountRef}
                label={`Amount (${currency})`}
                size="form"
                placeholder="0.00"
                inputMode="decimal"
                autoComplete="off"
                className="rec-form__amount"
                value={draft.amountText}
                error={errors.amount}
                onChange={(e) => set('amountText', e.target.value, 'amount')}
              />
              {repeats}
            </div>
          )}

          {/* Labelled above like every other field, so the panel keeps one rhythm. */}
          <div className="rec-form__group">
            <span className="rec-form__group-label" aria-hidden="true">
              Icon
            </span>
            <button
              ref={iconRowRef}
              type="button"
              className="rec-form__icon-row hoverable"
              style={{ minHeight: iconTarget }}
              aria-label={`Icon, ${iconValue}`}
              aria-haspopup="dialog"
              onClick={() => setIconOpen(true)}
            >
              <span className="rec-form__icon-value">
                <CatalogueIconImg src={pickedIcon?.icon_url ?? bill?.iconUrl} size={20} />
                <span className="rec-form__icon-text">{iconValue}</span>
              </span>
              <Icon glyph={ChevronRight} size={16} />
            </button>
          </div>

          {fromTxn !== null ? (
            <Field
              ref={sinceRef}
              label="Paying since"
              type="date"
              size="form"
              value={draft.payingSince ?? ''}
              error={errors.payingSince}
              onChange={(e) => set('payingSince', e.target.value === '' ? null : e.target.value, 'payingSince')}
            />
          ) : (
            <div className={pairs ? 'rec-form__pair' : 'rec-form__stack'}>
              <Field
                ref={nextDueRef}
                label="Next due"
                type="date"
                size="form"
                value={draft.nextDue}
                error={errors.nextDue}
                onChange={(e) => set('nextDue', e.target.value as LocalDate, 'nextDue')}
              />
              {paidFrom}
            </div>
          )}

          {mode.kind === 'create' && (
            <div className="rec-form__history">
              {/* The checkbox's hit target is wider than its 18px box; the
                  offset pulls the box onto the fields' left edge at every
                  width, and the note sits under the label text. */}
              <div className="rec-form__check" style={{ '--rec-check-target': `${checkTarget}px` } as CSSProperties}>
                <label className="rec-form__check-row">
                  <Checkbox
                    label="Apply to past transactions"
                    checked={draft.applyToPast}
                    disabled={draft.accountId === null}
                    describedBy={applyNoteId}
                    onChange={(v) => set('applyToPast', v, 'payingSince')}
                  />
                  <span className="rec-form__check-title">Apply to past transactions</span>
                </label>
                <p id={applyNoteId} className="note rec-form__note">
                  {applyNote}
                </p>
              </div>
              {/* Sent only with Apply to past (Ruling 19), so shown only then. */}
              {draft.applyToPast && draft.accountId !== null && (
                <Field
                  ref={sinceRef}
                  label="Paying since"
                  type="date"
                  size="form"
                  max={draft.nextDue || undefined}
                  value={draft.payingSince ?? ''}
                  error={errors.payingSince}
                  onChange={(e) => set('payingSince', e.target.value === '' ? null : e.target.value, 'payingSince')}
                />
              )}
            </div>
          )}

          {actions('foot')}
        </Panel>
      </form>

      <Overlay
        open={iconOpen}
        onClose={() => setIconOpen(false)}
        label="Icon"
        placement="sheet"
        size="wide"
        restoreFocusTo={iconRowRef}
      >
        <IconCataloguePicker
          value={pickedIcon?.slug ?? draft.icon}
          onPick={(icon) => {
            setPickedIcon(icon)
            set('icon', icon.slug)
            setIconOpen(false)
          }}
        />
      </Overlay>

      <ConfirmDialog
        open={leaving !== null}
        title={DISCARD.title}
        body={DISCARD.body}
        confirmLabel={DISCARD.confirm}
        cancelLabel={DISCARD.keep}
        destructive
        mode="discard_recurring_form"
        onCancel={() => setLeaving(null)}
        onConfirm={async () => {
          const to = leaving ?? backTo
          done.current = true
          setLeaving(null)
          await navigate(to)
        }}
      />

      <AlertDialog
        open={stillSaving}
        title={STILL_SAVING.title}
        body={STILL_SAVING.body}
        acknowledge="OK"
        onClose={() => setStillSaving(false)}
      />

      {askFindPast !== null && (
        <FindPastAsk bill={askFindPast} onDone={() => void navigate(recurringDetailPath(askFindPast.id), { replace: true })} />
      )}
    </div>
  )
}

/**
 * What the bill takes from the transaction, shown and not editable: the server
 * derives the amount and the account from it and never reads them from the
 * form (Ruling 20), so an input here would be an edit the save drops. The
 * category is the transaction's own; it is not sent either. An inflow says so,
 * because the amount is shown unsigned (D6).
 */
function TransactionFacts({ txn }: { txn: SourceTxn }) {
  const accounts = useAccounts()
  const categories = useCategories()
  const accountLine = (() => {
    if (accounts.status === 'success' || accounts.status === 'stale') {
      const a = accounts.data.find((x) => x.id === txn.accountId)
      return a ? accountLabel(a) : 'Unknown account'
    }
    return accounts.status === 'loading' ? null : 'Unavailable'
  })()
  const categoryLine = (() => {
    if (txn.categoryId === null) return 'No category'
    if (categories.status === 'success' || categories.status === 'stale') {
      return categories.data.find((c) => c.id === txn.categoryId)?.name ?? 'Unknown category'
    }
    return categories.status === 'loading' ? null : 'Unavailable'
  })()
  return (
    <div className="rec-form__facts">
      <dl className="rec-form__facts-list" aria-label="From this transaction">
        <div className="rec-form__fact">
          <dt>Amount</dt>
          <dd>
            <Money cents={Math.abs(txn.amountCents)} currency={txn.currency} />
            {txn.amountCents > 0 && <span className="rec-form__fact-cue"> · Income</span>}
          </dd>
        </div>
        <div className="rec-form__fact">
          <dt>Paid from</dt>
          <dd>{accountLine ?? <Skeleton height={14} width={120} />}</dd>
        </div>
        <div className="rec-form__fact">
          <dt>Category</dt>
          <dd>{categoryLine ?? <Skeleton height={14} width={90} />}</dd>
        </div>
      </dl>
      <p className="note">Taken from the transaction, so they can't be changed here.</p>
    </div>
  )
}

/**
 * After an edit that moved the account or the frequency, past transactions may
 * now match (iOS `offersMatchHistoryFollowUp`). Task 10's `findPast` does the
 * looking; the outcome is a toast, because the screen then leaves.
 */
function FindPastAsk({ bill, onDone }: { bill: Bill; onDone: () => void }) {
  const { findPast } = usePeriodLinks(bill)
  const toast = useToast()
  return (
    <ConfirmDialog
      open
      title={FIND_PAST_ASK.title}
      body={FIND_PAST_ASK.body}
      confirmLabel={FIND_PAST_ASK.confirm}
      pendingLabel="Looking…"
      cancelLabel={FIND_PAST_ASK.cancel}
      mode="find_past_after_recurring_edit"
      onCancel={onDone}
      onConfirm={async () => {
        try {
          const linked = await findPast()
          toast.show({ kind: 'success', message: findPastCopy(linked) })
        } catch (e) {
          toast.show({ kind: 'error', message: e instanceof PeriodLinkError ? e.message : FIND_FAILED })
        }
        onDone()
      }}
    />
  )
}
